import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import * as readApi from '../api/readApi';
import { formatDate } from '../utils/dateFormat';
import { toUtcMs } from '../utils/dateTimeUtils';
import { tournamentVisibleToUser } from '../utils/groupVisibility';
import GoogleAd from '../components/GoogleAd';
import LoadingBlock from '../components/LoadingBlock';
import './TournamentList.css';

export default function TournamentList() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { gameId } = useGame();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'completed', 'upcoming'

  useEffect(() => {
    fetchTournaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  async function fetchTournaments() {
    try {
      const data = await readApi.getTournamentList(gameId);
      setTournaments(data.tournaments || []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  }

  /** User can see open tournaments, or group/apartment-scoped tournaments (approved + in list). */
  function visibleToUser(tournament) {
    return tournamentVisibleToUser(tournament, userProfile);
  }

  function getTournamentStatus(tournament) {
    if (tournament.status === 'completed') return 'completed';
    if (tournament.status === 'active') return 'active';
    const startMs = toUtcMs(tournament.startDate);
    if (startMs != null && startMs > Date.now()) return 'upcoming';
    return 'active';
  }

  const visibleTournaments = tournaments.filter(visibleToUser);
  const filteredTournaments = visibleTournaments.filter(tournament => {
    if (filter === 'all') return true;
    const status = getTournamentStatus(tournament);
    return status === filter;
  });

  if (loading) {
    return <LoadingBlock message="Loading tournaments…" skeletonRows={5} fullPage />;
  }

  return (
    <div className="tournament-list-container">
      <div className="tournament-list-header">
        <h1>Tournaments</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>

      <GoogleAd slotKey="BANNER" className="google-ad-banner" />

      <div className="tournament-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All Tournaments
        </button>
        <button
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={filter === 'completed' ? 'active' : ''}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
        <button
          className={filter === 'upcoming' ? 'active' : ''}
          onClick={() => setFilter('upcoming')}
        >
          Upcoming
        </button>
      </div>

      <div className="tournaments-grid">
        {filteredTournaments.length === 0 ? (
          <div className="no-tournaments">
            <p>No tournaments found.</p>
          </div>
        ) : (
          filteredTournaments.map(tournament => {
            const status = getTournamentStatus(tournament);
            return (
              <div key={tournament.id} className="tournament-card">
                <div className="tournament-card-header">
                  <h3>{tournament.name}</h3>
                  <span className={`status-badge status-${status}`}>
                    {status === 'completed' ? 'Completed' : status === 'active' ? 'Active' : 'Upcoming'}
                  </span>
                </div>
                <p className="tournament-description">{tournament.description || 'No description'}</p>
                {tournament.location && (
                  <p className="tournament-location">📍 {tournament.location}</p>
                )}
                {tournament.startDate && (
                  <p className="tournament-date">
                    {formatDate(tournament.startDate)}
                    {tournament.endDate && ` - ${formatDate(tournament.endDate)}`}
                  </p>
                )}
                <div className="tournament-actions">
                  <button
                    onClick={() => navigate(`/tournament/${tournament.id}/dashboard`)}
                    className="btn-primary"
                  >
                    View Dashboard
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

